import { useQueryClient } from "@tanstack/react-query";
import { produce } from "immer";

import type {
  DetailedWorkspace,
  Workspace,
  WorkspaceAccount,
} from "common/types";

import getQueryKeys from "./getQueryKeys";

export default function useWorkspaceCacheUpdate() {
  const queryClient = useQueryClient();

  function updateCachedWorkspace(
    workspaceId: string,
    updater: (workspace: DetailedWorkspace) => void,
  ) {
    const detailedKey = getQueryKeys(workspaceId).workspaceKeys.base;
    const listKey = ["workspaces"];

    // Update detailed workspace if cached
    if (queryClient.getQueryData<DetailedWorkspace>(detailedKey)) {
      queryClient.setQueryData(
        detailedKey,
        (prevWorkspace: DetailedWorkspace) => produce(prevWorkspace, updater),
      );
    }

    // Update workspace in list
    queryClient.setQueryData(listKey, (prevWorkspaces: Workspace[]) =>
      produce(prevWorkspaces, (draftWorkspaces) => {
        const workspace = draftWorkspaces.find(({ id }) => id === workspaceId);

        if (workspace) {
          updater(workspace as DetailedWorkspace);
        }
      }),
    );
  }

  function updateCachedWorkspaceAccounts(
    workspaceId: string,
    updateWorkspaceAccounts: (accounts: WorkspaceAccount[]) => void,
  ) {
    const queryKey = getQueryKeys(workspaceId).workspaceKeys.accounts();

    if (queryClient.getQueryData(queryKey)) {
      queryClient.setQueryData(queryKey, (prevAccounts: WorkspaceAccount[]) =>
        produce(prevAccounts, updateWorkspaceAccounts),
      );
    }
  }

  return {
    updateCachedWorkspace,
    updateCachedWorkspaceAccounts,
  };
}
